import { GRENADA_PARISHES } from '../constants/parishes';

/** Parish name → delivery fee in cents (only parishes with a positive fee). */
export type DeliveryParishRates = Record<string, number>;

export function parseDeliveryParishRates(raw: unknown): DeliveryParishRates | undefined {
  if (raw == null) return undefined;
  let obj: unknown = raw;
  if (typeof raw === 'string') {
    try {
      obj = JSON.parse(raw);
    } catch {
      return undefined;
    }
  }
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) return undefined;
  const out: DeliveryParishRates = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const cents = Math.round(Number(value));
    if (cents > 0 && (GRENADA_PARISHES as readonly string[]).includes(key)) {
      out[key] = cents;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export function serializeDeliveryParishRates(
  rates: DeliveryParishRates | undefined,
): Record<string, number> | undefined {
  if (!rates) return undefined;
  const cleaned: Record<string, number> = {};
  for (const [key, value] of Object.entries(rates)) {
    const cents = Math.round(Number(value));
    if (cents > 0 && (GRENADA_PARISHES as readonly string[]).includes(key)) {
      cleaned[key] = cents;
    }
  }
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

/** Fee for one product to the given parish (parish rate overrides flat). */
export function productDeliveryFeeCents(
  product: {
    deliveryPriceCents?: number | null;
    deliveryParishRates?: DeliveryParishRates | unknown;
  },
  parish: string,
): number {
  const rates =
    typeof product.deliveryParishRates === 'object' && product.deliveryParishRates !== null
      ? parseDeliveryParishRates(product.deliveryParishRates)
      : parseDeliveryParishRates(product.deliveryParishRates);
  const parishRate = rates?.[parish];
  if (parishRate != null && parishRate > 0) return parishRate;
  const flat = product.deliveryPriceCents;
  if (flat != null && flat > 0) return flat;
  return 0;
}

/** One delivery charge per seller per order (max fee among their cart products). */
export function cartDeliveryFeeCents(
  lines: { productId: string; vendorId: string }[],
  productsById: Map<
    string,
    { vendorId?: string; deliveryPriceCents?: number; deliveryParishRates?: unknown }
  >,
  parish: string,
): number {
  const maxByVendor = new Map<string, number>();
  for (const line of lines) {
    const product = productsById.get(line.productId);
    if (!product) continue;
    const fee = productDeliveryFeeCents(product, parish);
    const vendorId = line.vendorId;
    maxByVendor.set(vendorId, Math.max(maxByVendor.get(vendorId) ?? 0, fee));
  }
  let total = 0;
  for (const fee of maxByVendor.values()) total += fee;
  return total;
}

export function formatDeliveryFeeSummary(
  feeCents: number,
  parish: string,
  currency = 'XCD',
): string {
  if (feeCents <= 0) return 'Free delivery from seller';
  const amount = (feeCents / 100).toFixed(feeCents % 100 === 0 ? 0 : 2);
  return `Delivery to ${parish}: ${currency} ${amount}`;
}
